import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../app/store";
import { APP_STATUS, BASE_URL } from "../../helpers/constants";
import { TypeExtraInput } from "../../pages/payment_links/NewPaymentLink";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type TypeTicketType = {
  id?: number | string;
  event_ticket_id?: number | string;
  name: string;
  price: number;
  original_price: number;
  min_qty: number;
  max_qty: number;
  capacity: number;
  sold?: number;
  note: string | null;
  position?: number;
};

export type TypeEventsTicketItem = {
  id: number | string;
  ref: string;
  title: string;
  tagline: string | null;
  description: string | null;
  image: string | null;
  venue: string | null;
  event_mode: string; // "physical" | "online"
  online_link: string | null; // stripped from the public details endpoint
  event_date: string | null; // ISO timestamp - the first day
  end_date: string | null; // ISO timestamp - the last day, same as event_date when single day
  start_time: string | null; // HH:mm - opening day
  end_time: string | null; // HH:mm - opening day
  end_day_start_time: string | null; // HH:mm - closing day
  end_day_end_time: string | null; // HH:mm - closing day
  category: string | null;
  type: string; // "public" | "private"
  ticket_types: TypeTicketType[];
  extra_fields: TypeExtraInput[] | null;
  seller_note: string | null;
  buyer_note: string | null;
  viewpt_disabled?: boolean;
  created_at: string;
};

/* what prismaCursorPagination returns, wrapped by apiSuccessResponse */
type TypePaginatedEvents = {
  items?: TypeEventsTicketItem[];
  nextCursor?: number | string | null;
  lastCursor?: number | string | null;
  totalItems?: number;
  totalPages?: number;
};

type TypeEventsTicketItemsState = {
  eventsTicketItems: TypeEventsTicketItem[];
  loadingItemsStatus: string;
  cursor: number | string | null;
  hasMore: boolean;
  totalItems: number;
};

const initialState: TypeEventsTicketItemsState = {
	eventsTicketItems: [],
	loadingItemsStatus: APP_STATUS.IDLE,
	cursor: null,
	hasMore: false,
	totalItems: 0,
};

/* ------------------------------------------------------------------ */
/* Thunk                                                               */
/* ------------------------------------------------------------------ */

export const fetchEventsTicketItems = createAsyncThunk(
	"eventsTicketItems/fetchEventsTicketItems",
	async (_, { getState, rejectWithValue }) => {
		const state = getState() as RootState;
		const accessToken = state.auth.accessToken;
		const cursor = state.eventsTicketItems.cursor;

		try {
			const response = await axios({
				method: "get",
				url: BASE_URL + "/api/donation-link/get_event_tickets",
				headers: {
					Authorization: "Bearer " + accessToken,
				},
				params: cursor != null ? { cursor } : {},
			});

			// apiSuccessResponse wraps the payload: { status, message, data }
			return response.data?.data ?? response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

/* ------------------------------------------------------------------ */
/* Slice                                                               */
/* ------------------------------------------------------------------ */

const eventsTicketItemsSlice = createSlice({
	name: "eventsTicketItems",
	initialState,
	reducers: {
		resetCursor: (state) => {
			state.eventsTicketItems = [];
			state.cursor = null;
			state.hasMore = false;
			state.totalItems = 0;
			state.loadingItemsStatus = APP_STATUS.IDLE;
		},
		// ids arrive as BigInt-serialised strings, so allow both
		removeEventsTicketItem: (state, action: PayloadAction<number | string>) => {
			state.eventsTicketItems = state.eventsTicketItems.filter(
				(item) => String(item.id) !== String(action.payload)
			);
			state.totalItems = Math.max(state.totalItems - 1, 0);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchEventsTicketItems.pending, (state) => {
				state.loadingItemsStatus = APP_STATUS.PENDING;
			})
			.addCase(fetchEventsTicketItems.fulfilled, (state, action) => {
				const payload = (action.payload || {}) as TypePaginatedEvents;

				// rows come back under "items"
				const incoming = payload.items ?? [];

				// a 304 can arrive with an empty body - don't wipe the list
				if (incoming.length === 0 && state.eventsTicketItems.length > 0) {
					state.loadingItemsStatus = APP_STATUS.SUCCESS;
					return;
				}

				// first load replaces, "load more" appends
				state.eventsTicketItems =
          state.cursor === null
          	? incoming
          	: [...state.eventsTicketItems, ...incoming];

				state.cursor = payload.nextCursor ?? null;
				state.totalItems = payload.totalItems ?? incoming.length;

				// on the final page nextCursor equals lastCursor
				state.hasMore =
          payload.nextCursor != null &&
          String(payload.nextCursor) !== String(payload.lastCursor);

				state.loadingItemsStatus = APP_STATUS.SUCCESS;
			})
			.addCase(fetchEventsTicketItems.rejected, (state) => {
				state.loadingItemsStatus = APP_STATUS.ERROR;
			});
	},
});

export const { resetCursor, removeEventsTicketItem } =
  eventsTicketItemsSlice.actions;

/* ------------------------------------------------------------------ */
/* Selectors                                                           */
/* ------------------------------------------------------------------ */

export const selectEventsTicketItems = (state: RootState) =>
	state.eventsTicketItems.eventsTicketItems;

export const selectEventsTicketItemsHasMore = (state: RootState) =>
	state.eventsTicketItems.hasMore;

export const selectEventsTicketItemsTotal = (state: RootState) =>
	state.eventsTicketItems.totalItems;

export const selectEventsTicketItemById = (
	state: RootState,
	ref: string
): TypeEventsTicketItem | undefined =>
	state.eventsTicketItems.eventsTicketItems.find((item) => item.ref === ref);

export default eventsTicketItemsSlice.reducer;
